<script setup>
/**
 * TelaHeader — a barra do topo de TODA tela da plataforma (2026-08-08).
 *
 * Nasceu de uma queixa sistêmica do Gustavo: "essa coluna 'Conversas' está esquisita,
 * ocupando a parte de cima da plataforma. Vamos precisar manter o padrão para todas as
 * telas: colocar o header, dar um espacinho e aí sim alinhar o início da sidebar com o
 * restante da outra coluna."
 *
 * O defeito era real e estrutural: metade das telas (Início, Métricas, Qualificação ›
 * Desempenho, Assistente) abria com uma barra de 56px carregando o CAMINHO, e a outra metade
 * (Conversas, Contatos, Equipe, Qualificação › Fila) começava com o conteúdo colado na borda
 * da janela. Duas telas do mesmo produto abrindo de forma diferente é o que faz o conjunto
 * parecer remendo — e sem a barra o topo da coluna de conteúdo não tem com o que se alinhar.
 *
 * ⚠️ ESTE COMPONENTE É A FONTE ÚNICA DA BARRA. O markup dela existia copiado em quatro
 * arquivos; a quinta cópia é que teria divergido no primeiro ajuste. Tela nova usa este
 * componente — não recria a `<header>`.
 *
 * Anatomia (aprovada por ele em Qualificação › Desempenho e replicada daqui em diante):
 * barra de 56px fechada por um fio, caminho em 13px, o último nível em tinta cheia e os
 * anteriores apagados. O TÍTULO de verdade nasce abaixo, dentro do conteúdo, depois de um
 * respiro — a barra é chrome, lida de relance; título grande nela obriga o olho a subir até
 * o canto para saber o que está vendo e repete o item já aceso na navegação.
 */
import AjudaBotao from './AjudaBotao.vue';
// custom (2026-08-11): a barra oculta muda a quina esquerda deste cabeçalho — ver a nota do
// `<header>` no template. O estado é um ref de MÓDULO, então ler daqui não cria acoplamento
// de componente: é a mesma fonte que a própria barra e o botão flutuante consultam.
import { useSidebarOculta } from 'dashboard/components-next/sidebar/provider';

defineProps({
  /**
   * Os níveis do caminho, da raiz à folha: `['Qualificação', 'Desempenho']`.
   * Um nível só (`['Conversas']`) rende uma migalha de um item, que é o caso da maioria
   * das telas — e continua sendo a mesma peça, não um segundo desenho.
   */
  caminho: { type: Array, required: true },
  /** Ícone lucide opcional antes do primeiro nível (ex.: `i-lucide-house` no Início). */
  icone: { type: String, default: '' },
  /**
   * Barra grudada no topo enquanto o conteúdo rola. Só faz sentido quando a tela inteira
   * é um scroller único (Início); em tela com coluna interna rolável o sticky não tem o
   * que acompanhar.
   */
  fixa: { type: Boolean, default: false },
  /**
   * Esconde o "Ajuda" da quina direita. Existe para a exceção, não para a regra: a ajuda
   * some numa tela e a pessoa que precisou uma vez volta a procurar no mesmo canto e não
   * acha. Só usar onde a barra tem uma razão forte para ficar limpa.
   */
  semAjuda: { type: Boolean, default: false },
});

// Depois do `defineProps` de propósito: o `vue/define-macros-order` exige que a macro seja a
// primeira instrução do `<script setup>`.
const { sidebarOculta } = useSidebarOculta();
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -- CRM é pt-BR locked (v1) -->
  <!-- ⚠️ A QUINA ESQUERDA ABRE ESPAÇO QUANDO A BARRA ESTÁ OCULTA (2026-08-11).
       Defeito que o Gustavo apontou clicando no botão: com a barra escondida, o botão
       flutuante de reabrir vive em `fixed left-3` (x 12→44) e este cabeçalho começa em
       `px-5` (x 20) — os dois ocupavam o MESMO lugar, e o botão cobria a casinha da migalha.
       Ele leu como "fica bugado, aparece a casinha por trás e aparece o ícone ali", e é
       exatamente isso: dois ícones empilhados no mesmo ponto.
       O conserto tem duas metades e as duas são necessárias:
         · `pl-14` (56px = 12 do recuo + 32 do botão + 12 de respiro) tira a migalha de baixo
           dele em vez de deixar as duas peças disputando o pixel;
         · a CASINHA some (ver o `v-if` abaixo) — com o botão ali, ela seria o segundo ícone
           da mesma fileira dizendo outra coisa. Some só quando a barra está oculta; com a
           barra aberta nada disso se aplica e o cabeçalho é o de sempre.
       Quando a barra volta, os dois voltam juntos. -->
  <header
    class="flex min-h-14 flex-shrink-0 items-center justify-between gap-2 border-b border-n-weak py-2"
    :class="[
      fixa ? 'sticky top-0 z-20 bg-n-background/85 backdrop-blur' : '',
      sidebarOculta ? 'pl-14 pr-3 sm:pr-5' : 'px-3 sm:px-5',
    ]"
  >
    <!-- ⚠️ `leading-4` (16px) e NÃO `leading-none`, e isso é acabamento, não capricho: com
         `leading-none` a caixa do texto tem 13px e a do ícone tem 16px, então `items-center`
         alinha duas caixas de alturas diferentes e o rótulo cai meio pixel abaixo do ícone.
         Ele pegou isso na tela: "a palavra parece desalinhada do ícone; são detalhes que
         passam falta de acabamento".
         Igualar a altura da linha à do ícone faz as duas caixas terem 16px, e aí centralizar
         é exato — não depende de ajuste óptico chutado. -->
    <!-- ⚠️ 13 → 16px em 2026-08-09, a pedido do Gustavo: "deixa essa fonte do mesmo
         tamanho da sidebar". A sidebar subiu para 16px na mesma sessão, e uma migalha de
         13px ao lado de um menu de 16 lê como legenda, não como o nome da tela.
         `leading-5` e `size-5` ANDAM JUNTOS, e é a mesma regra de sempre: a caixa do texto
         tem de ter a MESMA altura da caixa do ícone, senão `items-center` centraliza duas
         caixas de alturas diferentes e o rótulo cai meio pixel abaixo — foi exatamente o
         defeito que ele apontou aqui ("a palavra parece desalinhada do ícone; são detalhes
         que passam falta de acabamento"). Com 16px de fonte a dupla vira 20/20.
         `leading-4` com fonte 16 seria altura de linha 1,0 e cortaria o "ç" de Ações. -->
    <!-- ⚠️ 16 → 14px em 2026-08-11, DESCENDO junto com a sidebar (17 → 14px). A regra que
         este arquivo já registrava continua valendo — "do mesmo tamanho da sidebar" —, só que
         agora ela desce em vez de subir: migalha e menu são a mesma camada (chrome), e chrome
         que iguala o corpo do conteúdo faz a moldura brigar com o quadro.
         `leading-5` fica, e é por isso que a descida é segura: a caixa do texto continua com
         20px, igual à da casinha ao lado, então o alinhamento que ele cobrou ("a palavra
         parece desalinhada do ícone") não volta. O que muda é o desenho da letra dentro de
         uma caixa que não mudou — que é exatamente como se mexe em corpo sem mexer em grade.
         ⚠️ A CASINHA FICA EM 20px de propósito. Ela é a âncora da tela e o Gustavo a usou
         como RÉGUA para o botão de ocultar da barra ("tem que ficar do tamanho da casinha") —
         encolhê-la agora invalidaria a referência que ele acabou de dar. Ela sustenta 1,43× o
         corpo do texto sem pesar porque a tinta é `slate-9`, a mais apagada da escala: quem
         segura o peso visual aqui é o TOM, não o tamanho. -->
    <nav
      class="flex items-center gap-1.5 text-[14px] leading-5 min-w-0"
      aria-label="Caminho"
    >
      <!-- A casinha só existe com a barra ABERTA. Com a barra oculta, o botão flutuante de
           reabrir ocupa esse ponto da tela — dois ícones no mesmo lugar era o defeito. -->
      <span
        v-if="icone && !sidebarOculta"
        class="size-5 text-n-slate-9 shrink-0"
        :class="icone"
        aria-hidden="true"
      />
      <template v-for="(nivel, i) in caminho" :key="nivel">
        <!-- A seta separa, e some antes do primeiro nível — chevron solto na frente da
             migalha sugere que existe um nível acima que não existe. -->
        <span
          v-if="i > 0"
          class="i-lucide-chevron-right size-4 text-n-slate-9 shrink-0"
          aria-hidden="true"
        />
        <!-- A FOLHA em tinta cheia, os pais apagados: é o degrau de tom que diz onde você
             está sem precisar de tamanho maior. -->
        <span
          class="truncate"
          :class="
            i === caminho.length - 1
              ? 'font-500 text-n-slate-12'
              : 'text-n-slate-10'
          "
        >
          {{ nivel }}
        </span>
      </template>
    </nav>

    <!-- Ações da tela (busca, ordenação, botões). Ficam na MESMA barra em vez de numa
         segunda faixa abaixo — foi o que permitiu a tela de Contatos, que tinha um
         cabeçalho de 88px só seu, entrar no padrão de 56px sem perder nenhum controle. -->
    <!-- A QUINA DIREITA. Ações da tela (busca, ordenação, botões) ficam na MESMA barra em
         vez de numa segunda faixa abaixo — foi o que permitiu a tela de Contatos, que tinha
         um cabeçalho de 88px só seu, entrar no padrão de 56px sem perder controle nenhum.
         O "Ajuda" fecha a fileira, sempre por último: é o item que não pertence à tela, e
         sim ao produto. Pedido do Gustavo com a referência (Basepoint) na mão — ver a nota
         do AjudaBotao sobre por que o "Configure" dela NÃO veio junto. -->
    <div class="flex shrink-0 items-center gap-1 sm:gap-2">
      <slot v-if="$slots.acoes" name="acoes" />
      <AjudaBotao v-if="!semAjuda" />
    </div>
  </header>
</template>
